import { useState, useEffect, forwardRef, useImperativeHandle, useCallback, useRef } from 'react';
import { userSession } from '../utils/userSession.js';
import { XMarkIcon, HomeIcon, MegaphoneIcon, BanknotesIcon, Cog6ToothIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import UserDashboardHome from './UserDashboardHome.js';
import UserDashboardPromoter from './UserDashboardPromoter.js';
import UserDashboardPartner from './UserDashboardPartner.js';
import UserDashboardAdmin from './UserDashboardAdmin.js';

const UserDashboard = forwardRef(({ isOpen, onClose, user }, ref) => {
  const [userEvents, setUserEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentView, setCurrentView] = useState('home');
  const scrollContainerRef = useRef(null);
  const userId = user?.userId || userSession.getUserId();
  const userInitials = user ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase() : '';
  const userName = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '';

  useImperativeHandle(ref, () => ({
    handleEventsChange: () => {
      fetchUserEvents();
    }
  }));

  const fetchUserEvents = useCallback(async () => {
    try {
      setIsLoading(true);
      
      if (!userId) {
        setUserEvents([]);
        setIsLoading(false);
        return;
      }

      const response = await fetch(
        `https://r7ej0e9i28.execute-api.ap-southeast-2.amazonaws.com/dev/userevents/${userId}`
      );

      const text = await response.text();
      const data = JSON.parse(text);
      const eventsArray = Array.isArray(data) ? data : [];
      setUserEvents(eventsArray);
    } catch (err) {
      setError('Failed to load your events');
      console.error('Error fetching user events:', err);
      setUserEvents([]);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (isOpen && userId) {
      fetchUserEvents();
    }
  }, [fetchUserEvents, isOpen, userId]);

  // Scroll to top when switching between views
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentView]);

  const handleEventsChange = () => {
    fetchUserEvents();
  };

  const handleSignOut = () => {
    // Batch operations
    const cleanup = () => {
      userSession.clearUser();
      window.location.reload();
    };
    requestAnimationFrame(cleanup);
  };

  if (!isOpen) return null;

  const renderFooterItem = (icon, text, view) => (
    <div 
      onClick={() => setCurrentView(view)}
      className={`flex flex-col items-center space-y-1 cursor-pointer ${
        currentView === view ? 'text-slate-900' : 'text-gray-500'
      }`}
    >
      {icon}
      <span className="text-xs">{text}</span>
    </div>
  );

  const renderContent = () => {
    switch (currentView) {
      case 'home':
        return <UserDashboardHome 
          savedEvents={userEvents} 
          isLoading={isLoading} 
          error={error}
          onEventsChange={handleEventsChange}
          key={userEvents.length}
        />;
      case 'promoter':
        return <UserDashboardPromoter />;
      case 'partner':
        return <UserDashboardPartner />;
      case 'admin':
        return <UserDashboardAdmin />;
      default:
        return <UserDashboardHome savedEvents={userEvents} isLoading={isLoading} error={error} />;
    }
  };

  return (
    <div className={`fixed inset-y-0 right-0 w-full sm:w-[600px] bg-gray-50 shadow-lg transform transition-transform duration-300 ease-in-out ${
      isOpen ? 'translate-x-0' : 'translate-x-full'
    } z-[110]`}>
      <div ref={scrollContainerRef} className="relative h-full overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white shadow-sm z-20">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center space-x-4">
              <div className="h-12 w-12 rounded-full bg-slate-900 flex items-center justify-center">
                <span className="text-white text-xl font-bold">{userInitials}</span>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">{userName}</h2>
                <p className="text-sm text-gray-500">My DriftCal</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={handleSignOut}
                className="flex flex-col items-center text-red-500 hover:text-gray-700"
              >
                <ArrowRightOnRectangleIcon className="h-6 w-6" />
                <span className="text-[10px]">Sign Out</span>
              </button>
              <button
                onClick={onClose}
                className="flex flex-col items-center text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="h-6 w-6" />
                <span className="text-[10px]">Close</span>
              </button>
            </div>
          </div>
          <div className="h-px bg-gradient-to-r from-transparent via-slate-600 to-transparent"></div>
        </div>
        
        <div className="p-6 pb-20">
          {renderContent()}
        </div>

        {/* Footer */}
        <div className="fixed bottom-0 left-0 right-0 border-t bg-white z-20" style={{ width: 'inherit' }}>
          <div className={`grid ${userSession.getUserId() === 1 ? 'grid-cols-4' : 'grid-cols-3'} gap-4 px-6 py-3`}>
            {renderFooterItem(
              <HomeIcon className="h-6 w-6" />,
              'Home',
              'home'
            )}
            {renderFooterItem(
              <MegaphoneIcon className="h-6 w-6" />,
              'Promoter',
              'promoter'
            )}
            {renderFooterItem(
              <BanknotesIcon className="h-6 w-6" />,
              'Partner',
              'partner'
            )}
            {userSession.getUserId() === 1 && renderFooterItem(
              <Cog6ToothIcon className="h-6 w-6" />,
              'Admin',
              'admin'
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

export default UserDashboard; 